import React, { useEffect, useState } from 'react';
import { useStore } from 'react-redux';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import loadable from '@loadable/component';

import { runOnEnterHandlers } from 'react-redux-app/lib/modules/routing/utils';

import { INTERNAL_ERROR_URI } from 'react-redux-app/lib/modules/routing/constants';

import {
  activeRequiredHandler,
  authRequiredHandler,
  checkCampaignsHandler,
  checkLangHandler,
  checkRedirectAfterLoginUrlHandler,
  externalCommandHandler,
  getWebmasterInfoHandler,
  initHandler,
} from './onEnterRouteHandlers';
import notActiveRequiredHandler from '../../auth/routes/onEnterRouteHandlers/notActiveRequired';
import {
  loadBannersHandler,
  loadSiteBannersExchangeHandler,
} from '../../banner/routes/onEnterRouteHandlers';
import {
  loadPayoutSettingsHandler,
  recheckXCardStatusHandler,
} from '../../payoutSettings/routes/onEnterRouteHandlers';
import {
  loadPostbackSettingsHandler,
  loadCampaignNamesHandler,
} from '../../postbackSettings/routes/onEnterRouteHandlers';
import loadSiteHandler from '../../site/routes/onEnterRouteHandlers/loadSite';
import loadCreatePreparationHandler from '../../siteBuilder/routes/onEnterRoutHandlers/loadCreatePreparation';
import loadFaviconHandler from '../../siteFavicon/routes/onEnterRoutHandlers/loadFavicon';
import loadFooterLinksHandler from '../../siteFooterLinks/routes/onEnterRouteHandlers/loadFooterLinks';
import loadSiteGoogleAnalyticsHandler from '../../siteGoogleAnalytics/routes/onEnterRouteHandlers/loadSiteGoogleAnalytics';
import loadSiteGoogleToolHandler from '../../siteGoogleTool/routes/onEnterRouteHandlers/loadSiteGoogleTool';
import loadStaticPagesHandler from '../../siteStaticPages/routes/onEnterRouteHandlers/loadStaticPages';
import loadTemplateConfigPreparationHandler from '../../siteTemplateConfiguration/routes/onEnterRoutHandlers/loadTemplateConfigPreparation';
import loadSiteTextsWithConfigHandler from '../../siteTexts/routes/onEnterRouteHandlers/loadSiteTextsWithConfig';
import loadBalanceSummaryHandler from '../../webmaster/routes/onEnterRouteHandlers/loadBalanceSummary';

import Layout from '../components/Layout';


const Loading = () => 'Loading...';

const options = { fallback: <Loading /> };

const InternalError = loadable(() => import('react-redux-app/lib/modules/internalError/components/InternalError'));
const InternalErrorPage = () => <InternalError fallbackLink="/" />;

const AffiliateToolsPage = loadable(() => import('../../affiliateTools/components/AffiliateToolsPage'), options);
const AffiliatedUsersPage = loadable(() => import('../../affiliatedUsers/components/AffiliatedUsersPage'), options);
const AffiliatedWebmastersPage = loadable(() => import('../../affiliatedWebmasters/components/AffiliatedWebmastersPage'), options);
const UnsuspendPage = loadable(() => import('../../auth/containers/UnsuspendPage'), options);
const BalanceAdjustmentsPage = loadable(() => import('../../balanceAdjustments/components/BalanceAdjustmentsPage'), options);
const BannersPage = loadable(() => import('../../banner/components/BannersPage'), options);
const OneSiteBannersPage = loadable(() => import('../../banner/components/OneSiteBannersPage'), options);
const CampaignsPage = loadable(() => import('../../campaign/components/CampaignsPage'), options);
const CampaignsStatsPage = loadable(() => import('../../campaignStats/components/CampaignsStatsPage'), options);
const LossesAndCompensationsPage = loadable(() => import('../../lossesAndCompensations/components/LossesAndCompensationsPage'), options);
const PayoutFeesPage = loadable(() => import('../../payout/components/PayoutFeesPage'), options);
const PayoutRequestsPage = loadable(() => import('../../payout/components/PayoutRequestsPage'), options);
const PayoutSettingsPage = loadable(() => import('../../payoutSettings/components/PayoutSettingsPage'), options);
const PostbackSettingsPage = loadable(() => import('../../postbackSettings/containers/PostbackSettingsPage'), options);
const RevenuePage = loadable(() => import('../../revenue/components/RevenuePage'), options);
const SitesPage = loadable(() => import('../../site/components/SitesPage'), options);
const SitePage = loadable(() => import('../../site/components/SitePage'), options);
const OneSiteAliasesPage = loadable(() => import('../../siteAlias/components/OneSiteAliasesPage'), options);
const SiteAliasesPage = loadable(() => import('../../siteAlias/components/SiteAliasesPage'), options);
const SiteArticlesPage = loadable(() => import('../../siteArticle/containers/SiteArticlesPage'), options);
const CreateSitePage = loadable(() => import('../../siteBuilder/components/CreateSitePage'), options);
const SiteParametersPage = loadable(() => import('../../siteBuilder/components/SiteParametersPage'), options);
const SiteFaviconPage = loadable(() => import('../../siteFavicon/components/FaviconPage'), options);
const SiteFooterLinksPage = loadable(() => import('../../siteFooterLinks/containers/SiteFooterLinksPage'), options);
const SiteGoogleAnalyticsPage = loadable(() => import('../../siteGoogleAnalytics/containers/SiteGoogleAnalyticsPage'), options);
const SiteGoogleToolPage = loadable(() => import('../../siteGoogleTool/containers/SiteGoogleToolPage'), options);
const SiteCustomTagsPage = loadable(() => import('../../siteSeo/components/SiteCustomTagsPage'), options);
const SiteSeoPage = loadable(() => import('../../siteSeo/components/SiteSeoPage'), options);
const SiteStaticPagesPage = loadable(() => import('../../siteStaticPages/containers/SiteStaticPagesPage'), options);
const TemplateConfigurationPage = loadable(() => import('../../siteTemplateConfiguration/containers/TemplateConfigurationPage'), options);
const SiteTextsPage = loadable(() => import('../../siteTexts/components/SiteTextsPage'), options);
const HowToStartPage = loadable(() => import('../../staticContent/containers/HowToStartPage'), options);
const EditProfilePage = loadable(() => import('../../webmaster/components/EditProfilePage'), options);
const BalanceSummaryPage = loadable(() => import('../../webmaster/components/BalanceSummaryPage'), options);

const routes = [];

const getDataLoader = (isLayout = false) => ({ component: Component }) => {
  const store = useStore();
  const location = useLocation();
  const navigate = useNavigate();

  const [isInitialLoadRequired, setIsInitialLoadRequired] = useState(isLayout);
  const [pathname, setPathname] = useState();

  const pathnameChanged = !isLayout && location.pathname !== pathname;

  useEffect(() => {
    if (isLayout && !isInitialLoadRequired) {
      return;
    }

    runOnEnterHandlers(
      isInitialLoadRequired,
      routes,
      location,
      store,
      navigate,
      () => {
        setIsInitialLoadRequired(false);
        setPathname(location.pathname);
      }
    );
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  return isInitialLoadRequired || pathnameChanged ? <Loading /> : <Component />;
};

const LayoutDataLoader = getDataLoader(true);
const DataLoader = getDataLoader();

routes.push(
  { path: INTERNAL_ERROR_URI, element: <InternalErrorPage /> },
  {
    path: '/:lang/webmaster-panel',
    element: <LayoutDataLoader component={Layout} />,
    onServerEnter: [
      externalCommandHandler,
      checkRedirectAfterLoginUrlHandler,
      checkLangHandler,
      initHandler,
      authRequiredHandler,
      checkCampaignsHandler,
      getWebmasterInfoHandler,
    ],
    children: [
      {
        path: 'unsuspend-me',
        element: <DataLoader component={UnsuspendPage} />,
        onEnter: [notActiveRequiredHandler],
      },
      {
        onEnter: [activeRequiredHandler],
        children: [
          { index: true, element: <Navigate to="stats" /> },
          { path: 'affiliate-tools', element: <DataLoader component={AffiliateToolsPage} /> },
          { path: 'users', element: <DataLoader component={AffiliatedUsersPage} /> },
          { path: 'webmasters', element: <DataLoader component={AffiliatedWebmastersPage} /> },
          { path: 'balance-adjustments', element: <DataLoader component={BalanceAdjustmentsPage} /> },
          {
            path: 'banners',
            element: <DataLoader component={BannersPage} />,
            onEnter: [loadBannersHandler],
          },
          { path: 'campaigns', element: <DataLoader component={CampaignsPage} /> },
          { path: 'stats', element: <DataLoader component={CampaignsStatsPage} /> },
          {
            path: 'losses-compensations',
            element: <DataLoader component={LossesAndCompensationsPage} />,
          },
          { path: 'requests', element: <DataLoader component={PayoutRequestsPage} /> },
          { path: 'fees', element: <DataLoader component={PayoutFeesPage} /> },
          {
            path: 'payout-settings',
            element: <DataLoader component={PayoutSettingsPage} />,
            onEnter: [loadPayoutSettingsHandler/* , recheckXCardStatusHandler */],
          },
          {
            path: 'postback-settings',
            element: <DataLoader component={PostbackSettingsPage} />,
            onEnter: [loadPostbackSettingsHandler, loadCampaignNamesHandler],
          },
          { path: 'revenue', element: <DataLoader component={RevenuePage} /> },
          { path: 'site-aliases', element: <DataLoader component={SiteAliasesPage} /> },
          { path: 'how-to-start', element: <DataLoader component={HowToStartPage} /> },
          { path: 'profile', element: <DataLoader component={EditProfilePage} /> },
          {
            path: 'balance-summary',
            element: <DataLoader component={BalanceSummaryPage} />,
            onEnter: [loadBalanceSummaryHandler],
          },
          { path: '*', element: <div>Not Found</div> },
          {
            path: 'sites',
            onEnter: [loadCreatePreparationHandler],
            children: [
              { index: true, element: <DataLoader component={SitesPage} /> },

              { path: 'create', element: <DataLoader component={CreateSitePage} /> },

              {
                path: ':siteId',
                element: <DataLoader component={SitePage} />,
                onEnter: [loadSiteHandler],
                children: [
                  {
                    path: 'settings',
                    children: [
                      { index: true, element: <SiteParametersPage /> },
                      {
                        path: 'template-configuration',
                        element: <TemplateConfigurationPage />,
                        onEnter: [loadTemplateConfigPreparationHandler],
                      },
                      {
                        path: 'google-analytics',
                        element: <SiteGoogleAnalyticsPage />,
                        onEnter: [loadSiteGoogleAnalyticsHandler],
                      },
                      {
                        path: 'google-webmaster-tools',
                        element: <SiteGoogleToolPage />,
                        onEnter: [loadSiteGoogleToolHandler],
                      },
                      {
                        path: 'favicon',
                        element: <SiteFaviconPage />,
                        onEnter: [loadFaviconHandler],
                      },
                      { path: 'aliases', element: <OneSiteAliasesPage /> },
                    ],
                  },

                  {
                    path: 'seo',
                    children: [
                      { index: true, element: <SiteSeoPage /> },
                      { path: 'custom-tags', element: <SiteCustomTagsPage /> },
                    ],
                  },

                  {
                    path: 'content',
                    children: [
                      { path: 'articles', element: <SiteArticlesPage /> },
                      {
                        path: 'pages',
                        element: <SiteStaticPagesPage />,
                        onEnter: [loadStaticPagesHandler],
                      },
                      {
                        path: 'footer-links',
                        element: <SiteFooterLinksPage />,
                        onEnter: [loadFooterLinksHandler],
                      },
                      {
                        path: 'texts',
                        element: <SiteTextsPage />,
                        onEnter: [loadSiteTextsWithConfigHandler],
                      },
                    ],
                  },

                  {
                    path: 'banners',
                    element: <OneSiteBannersPage />,
                    onEnter: [loadSiteBannersExchangeHandler],
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: '/webmaster-panel/*',
    element: <DataLoader component={() => {}} />,
    onEnter: [checkLangHandler],
  }
);

export default routes;
